import type { ReactElement } from 'react';
import styled from 'styled-components';
import { Box, FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { IconButton, ButtonSize, ButtonType } from '@ardoq/button';
import { IconName, WorkspaceIcon } from '@ardoq/icons';
import { ComponentDecoratorAsTag } from '@ardoq/basic-components';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { colors } from '@ardoq/design-tokens';
import type { APIComponentAttributes } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { t, type I18nLocale } from '@ardoq/i18n';
import { ResolvedComponentIcon } from '../../components/ResolvedComponentIcon';
import type { ComponentPreview } from '../types';

const VerticalRule = styled(Box)`
  align-self: stretch;
`;

type ComponentTypeTagProps = {
  component: APIComponentAttributes;
  scopeData: EnhancedScopeData;
};

const ComponentTypeTag = ({
  component,
  scopeData,
}: ComponentTypeTagProps): ReactElement => {
  const { icon, value, isImage, shadedColor, lightenedColor, contrastColor } =
    enhancedScopeDataOperations.getComponentTypeRepresentationDataWithColorShades(
      scopeData,
      component.model,
      component.typeId
    );

  return (
    <ComponentDecoratorAsTag
      shadedColor={shadedColor}
      lightenedColor={lightenedColor}
      contrastColor={contrastColor}
      icon={icon}
      value={value}
      isImage={isImage}
      label={component.type}
      noMargin
    />
  );
};

type PreviewHeaderProps = {
  preview: ComponentPreview;
  onExpand?: VoidFunction;
  i18nLocale: I18nLocale;
};

export const PreviewHeader = ({
  preview,
  onExpand,
  i18nLocale,
}: PreviewHeaderProps): ReactElement => (
  <FlexBox gap="small" justify="space-between" align="start">
    <FlexBox flexDirection="column" gap="small" minWidth="0" flexGrow={1}>
      <FlexBox gap="small" align="center" minWidth="0">
        <ResolvedComponentIcon
          component={preview.component}
          scopeData={preview.enhancedScopeData}
        />
        <Box minWidth="0" flexShrink={1}>
          <Text variant="text2Bold" truncate>
            {preview.component.name}
          </Text>
        </Box>
        <ComponentTypeTag
          component={preview.component}
          scopeData={preview.enhancedScopeData}
        />
      </FlexBox>
      <FlexBox gap="small" align="center" minWidth="0">
        <FlexBox gap="xsmall" align="center" minWidth="0">
          <WorkspaceIcon color={colors.textSubtle} />
          <Box minWidth="0" flexShrink={1}>
            <Text variant="caption" color="textSubtle" truncate>
              {preview.workspaceName}
            </Text>
          </Box>
        </FlexBox>
        {preview.parent && (
          <>
            <VerticalRule borderWidthLeft={1} borderColor="borderSubtle" />
            <FlexBox gap="xsmall" align="center" minWidth="0">
              <Text variant="caption" color="textSubtle">
                {t(
                  i18nLocale,
                  'componentOverview:miniOverview.parentLabel',
                  'Parent:'
                )}
              </Text>
              <ResolvedComponentIcon
                component={preview.parent}
                scopeData={preview.enhancedScopeData}
              />
              <Box minWidth="0" flexShrink={1}>
                <Text variant="captionBold" color="textSubtle" truncate>
                  {preview.parent.name}
                </Text>
              </Box>
            </FlexBox>
          </>
        )}
      </FlexBox>
    </FlexBox>
    {onExpand && (
      <IconButton
        buttonType={ButtonType.TERTIARY}
        buttonSize={ButtonSize.SMALL}
        iconName={IconName.EXPAND_ALL}
        label={t(
          i18nLocale,
          'componentOverview:miniOverview.expand',
          'Full overview'
        )}
        onClick={onExpand}
      />
    )}
  </FlexBox>
);
